import { CompatibleMetric, IntegrationInstance, ManifestIntegration, ManifestIntegrationSupportedCPI } from 'models'
import moment from 'moment'

/* Turns an instance's compatible metrics into the add-CPIs dialog payload, or null
 * to skip the dialog when there is nothing to offer. The slug fills `name`, the
 * field the dialog keys its checkboxes and activation callback on. */
export const buildCpiActivationOffer = (metrics: CompatibleMetric[]): ManifestIntegrationSupportedCPI[] | null => {
  if (!metrics.length) return null

  return metrics.map((metric) => ({
    name: metric.slug,
    display_name: metric.display_name,
    title: metric.title,
    description: metric.description
  }))
}

export function instanceOfManifestIntegration(
  object: ManifestIntegration | IntegrationInstance
): object is ManifestIntegration {
  return 'instances' in object
}

export const isIntegrationNew = (integration: ManifestIntegration | IntegrationInstance) => {
  if (integration) {
    if (instanceOfManifestIntegration(integration)) {
      // A publish date still ahead counts as new; taking the absolute gap aged it out instead.
      const diff = moment().diff(moment(integration.published_at), 'days') + 1

      if (diff < 30) {
        return true
      }
    } else return false
  }

  return false
}

export const JIRA_INTEGRATION_NAME = 'atlassian_jira'

/* Editing a Jira instance is withheld while the wizard's edit flow is being
 * built — the add wizard it would reuse cannot yet reopen a saved instance. */
export const isInstanceEditDisabled = (instance: IntegrationInstance) =>
  instance.integration_name === JIRA_INTEGRATION_NAME

export const INSTANCE_EDIT_DISABLED_HINT = 'Temporarily unavailable'

/* Where an instance's edit action goes. Jira's v2 instances are onboarded through
 * the wizard, which is also where an unfinished onboarding is completed; it is
 * keyed by the product instance name, not the v3 config id. */
export const getInstanceEditUrl = (instance: IntegrationInstance) => {
  if (instance.integration_name === JIRA_INTEGRATION_NAME && instance.is_v2 && instance.instance_name) {
    return `/settings/integrations/data-sources/${JIRA_INTEGRATION_NAME}/${instance.instance_name}/edit`
  }

  return `/settings/integrations/data-sources/${instance.integration_name}/${instance.id}/edit`
}

export const getVendorNameForInstance = (integrationName: string, supportedIntegrations: ManifestIntegration[]) => {
  const integration = supportedIntegrations.find((c) => c.name === integrationName)

  if (!integration) return ''

  return integration.vendor.display_name
}

export const getInstanceLogo = (integrationName: string, supportedIntegrations: ManifestIntegration[]) => {
  const integration = supportedIntegrations.find((c) => c.name === integrationName)

  if (!integration) return ''

  return integration.logo_path
}

export const hasConnectionConfigChanged = (
  currentValues: Record<string, string>,
  savedValues: Record<string, string>
) => {
  const excludedFields = ['name', 'description', 'integrationName', 'onyxia_product_instance']

  for (const [key, value] of Object.entries(currentValues)) {
    if (!excludedFields.includes(key)) {
      if (value !== savedValues[key]) {
        return true
      }
    }
  }

  return false
}

export const getConnectionConfig = (values: Record<string, string>) => {
  const configValues = { ...values }
  delete configValues.integrationName
  delete configValues.description
  delete configValues.name
  delete configValues.onyxia_product_instance

  return configValues
}
